// A turn's work, folded under its reply (S-509): the one line ("Worked for 37s · 9 steps"),
// and the steps it opens into: a row each, narration, output, and the turn's computer card.
import { Check, ChevronRight, Clock, ImageOff, Lock, Monitor, XCircle } from "lucide-react";
import { useState, type ReactNode } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@crew/ui/components/collapsible";
import { DotMatrix } from "@crew/ui/components/thread";
import { cn } from "@crew/ui/lib/utils";

/** The fold's open and close (S-509 M1): height to the panel's own, then a fade; nothing with reduced motion. */
export const foldMotion =
  "h-(--collapsible-panel-height) overflow-hidden transition-[height,opacity] duration-[220ms] ease-[cubic-bezier(0.23,1,0.32,1)] data-starting-style:h-0 data-starting-style:opacity-0 data-ending-style:h-0 data-ending-style:opacity-0 data-ending-style:duration-[160ms] motion-reduce:transition-none";

/**
 * The fold's line under a reply. `done`: "✓ Worked for 37s · 9 steps", with
 * "· 1 failed" in brick, a button that opens the steps. `quiet`: a check-in's,
 * in hint, no check. `live`: "Working for 12s" in moss, shimmering, with the
 * turn's progress line under it and `trailing` (Watch) beside it; slice 6f's
 * browser picture goes `below` the line, its moved-on row `above` it.
 */
export function WorkLine({
  tone,
  summary,
  failed = 0,
  open = false,
  current,
  above,
  below,
  trailing,
  onClick,
  className,
}: {
  tone: "live" | "done" | "quiet";
  summary: string;
  failed?: number;
  open?: boolean;
  /** Live: what it's doing now, from the progress line. */
  current?: ReactNode;
  /** Live: before what it's doing now (the browser it used, folded). */
  above?: ReactNode;
  /** Live: after what it's doing now (the browser's picture). */
  below?: ReactNode;
  trailing?: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  if (tone === "live") {
    return (
      <LiveWorkLine
        summary={summary}
        current={current}
        above={above}
        below={below}
        trailing={trailing}
        className={className}
      />
    );
  }
  return (
    <button
      type="button"
      data-slot="work-line"
      data-tone={tone}
      aria-expanded={open}
      onClick={onClick}
      className={cn(
        "pressable -ml-2 flex min-h-11 items-center gap-1.5 self-start rounded-nav px-2 text-meta outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-[30px]",
        tone === "quiet" ? "text-hint" : "text-muted-foreground",
        className,
      )}
    >
      {tone === "done" ? <Check aria-hidden className="size-3.5 shrink-0" strokeWidth={2} /> : null}
      <span>{summary}</span>
      {failed > 0 ? <span className="font-medium text-destructive">· {failed} failed</span> : null}
      <ChevronRight
        aria-hidden
        className={cn(
          "size-3 shrink-0 transition-transform duration-150 motion-reduce:transition-none",
          open && "rotate-90",
        )}
        strokeWidth={2}
      />
    </button>
  );
}

/** `WorkLine`'s live tone: "Working for 12s", the progress line, and what goes around it. */
function LiveWorkLine({
  summary,
  current,
  above,
  below,
  trailing,
  className,
}: {
  summary: string;
  current?: ReactNode;
  above?: ReactNode;
  below?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <div data-slot="work-line" data-tone="live" className={cn("flex flex-col gap-0.5", className)}>
      <div className="flex items-center gap-2.5">
        <span role="status" className="flex min-h-9 items-center gap-2 md:min-h-[30px]">
          <span className="flex w-[18px] items-center justify-center">
            <DotMatrix className="text-moss" />
          </span>
          <span className="text-meta font-medium text-moss shimmer tabular-nums motion-reduce:shimmer-none">
            {summary}
          </span>
        </span>
        {trailing}
      </div>
      {above ? <div className="pt-1 pb-1.5 pl-[26px]">{above}</div> : null}
      {current ? (
        <span className="flex items-center gap-2 pl-[26px] text-meta text-muted-foreground">
          {current}
        </span>
      ) : null}
      {below ? <div className="pt-2 pl-[26px]">{below}</div> : null}
    </div>
  );
}

/** The opened fold: its rows on the paper, icons in a 28px lane, durations in a 36px one. */
export function WorkPanel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div data-slot="work-panel" className={cn("flex flex-col", className)}>
      {children}
    </div>
  );
}

/** What the agent said between steps, as an aside beside a hairline. */
export function WorkNarration({ children }: { children: ReactNode }) {
  return (
    <div data-slot="work-narration" className="flex gap-3 py-2">
      <span className="flex w-7 shrink-0 justify-center">
        <span className="w-px self-stretch bg-input" />
      </span>
      <div className="min-w-0 flex-1 text-sm leading-[21px] text-copy">{children}</div>
    </div>
  );
}

const Lane = ({ children }: { children?: ReactNode }) => (
  <span className="flex w-7 shrink-0 justify-center text-muted-foreground [&_svg]:size-3.5">
    {children}
  </span>
);

/**
 * One step, or a run of one kind: its icon, its line, a failure's reason in
 * brick, and how long it took. With `children` it opens (S-509 M1) to show them.
 */
export function WorkRow({
  icon,
  label,
  detail,
  duration,
  failed = false,
  error,
  children,
}: {
  icon: ReactNode;
  label: string;
  detail?: string;
  duration?: string;
  failed?: boolean;
  error?: string | null;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const head = (
    <>
      <Lane>{failed ? <XCircle className="text-destructive" strokeWidth={2} /> : icon}</Lane>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5 py-2.5 text-left md:py-1.5">
        <span className="text-sm text-foreground">{label}</span>
        {detail ? <span className="truncate text-meta text-muted-foreground">{detail}</span> : null}
        {failed && error ? <span className="text-meta text-destructive">{error}</span> : null}
      </span>
      <span className="w-9 shrink-0 text-right text-xs text-hint tabular-nums">{duration}</span>
      <span className="flex w-3 shrink-0 text-hint">
        {children ? (
          <ChevronRight
            aria-hidden
            className={cn(
              "size-3 transition-transform duration-150 motion-reduce:transition-none",
              open && "rotate-90",
            )}
            strokeWidth={2}
          />
        ) : null}
      </span>
    </>
  );
  if (!children) {
    return (
      <div data-slot="work-row" className="flex min-h-11 items-center gap-3 md:min-h-8">
        {head}
      </div>
    );
  }
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        data-slot="work-row"
        className="pressable flex min-h-11 w-full items-center gap-3 rounded-nav outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-8"
      >
        {head}
      </CollapsibleTrigger>
      <CollapsibleContent className={foldMotion}>
        <div className="flex flex-col gap-1 pb-2 pl-10">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** A step's own line under its group: "canadiantire.ca/…/schwinn-ic3 · 3s". */
export function WorkMember({ label, duration }: { label: string; duration?: string }) {
  return (
    <div className="flex min-h-8 items-center gap-2">
      <span className="min-w-0 flex-1 truncate text-meta text-muted-foreground">{label}</span>
      <span className="w-9 shrink-0 text-right text-xs text-hint tabular-nums">{duration}</span>
      <span className="w-3 shrink-0" />
    </div>
  );
}

/** A command, a script or a result, in mono, in a well that scrolls past 240px. */
export function WorkOutput({ children, tone }: { children: ReactNode; tone?: "error" }) {
  return (
    <pre
      data-slot="work-output"
      className={cn(
        "max-h-60 overflow-auto overscroll-contain rounded-md bg-muted px-3 py-2.5 font-mono text-xs leading-[18px] break-words whitespace-pre-wrap",
        tone === "error" ? "text-destructive" : "text-foreground",
      )}
    >
      {children}
    </pre>
  );
}

/**
 * A picture of the page the agent's browser was on (slice 6f): 640 px wide,
 * kept at the frame's shape so the chat doesn't jump as it loads. `badge`
 * sits on it ("After step 4 · 3s ago"), and `site` and `title` under it.
 * `onError`: the picture is gone (pruned after 30 days), to show none instead.
 */
export function BrowserPicture({
  src,
  site,
  title,
  badge,
  trailing,
  onError,
  className,
}: {
  src: string;
  site?: string | null;
  title?: string | null;
  badge?: ReactNode;
  /** Beside the caption: "Step 7". */
  trailing?: ReactNode;
  onError?: () => void;
  className?: string;
}) {
  return (
    <figure
      data-slot="browser-picture"
      className={cn("flex max-w-[560px] flex-col gap-2", className)}
    >
      <div className="relative overflow-hidden rounded-tile bg-muted shadow-ring">
        <img
          src={src}
          alt={title ? `The browser: ${title}` : "The browser"}
          loading="lazy"
          decoding="async"
          onError={onError}
          className="aspect-[640/287] w-full object-cover object-top"
        />
        {badge ? (
          <span className="absolute bottom-2.5 left-2.5 inline-flex h-7 items-center gap-1.5 rounded-full bg-card/95 px-2.5 text-xs font-medium text-moss shadow-ring tabular-nums">
            <span aria-hidden className="size-1.5 rounded-full bg-moss" />
            {badge}
          </span>
        ) : null}
      </div>
      <PictureCaption site={site} title={title} trailing={trailing} />
    </figure>
  );
}

/** Under a picture: "bestbuy.ca · Results for …", and "Step 7" at the end. */
function PictureCaption({
  site,
  title,
  trailing,
}: {
  site?: string | null;
  title?: string | null;
  trailing?: ReactNode;
}) {
  // A page titled with its own host says it once.
  const page = title && title !== site ? (site ? `· ${title}` : title) : null;
  return (
    <figcaption className="flex min-w-0 items-baseline gap-1 text-meta">
      <span className="shrink-0 font-medium text-foreground">{site}</span>
      {page ? <span className="min-w-0 truncate text-muted-foreground">{page}</span> : null}
      {trailing ? (
        <span className="ml-auto shrink-0 pl-2 text-xs text-hint tabular-nums">{trailing}</span>
      ) : null}
    </figcaption>
  );
}

const NO_PICTURE = {
  sign_in: {
    icon: Lock,
    label: "Sign-in page",
    why: "no picture is kept of a page asking for a password",
  },
  gone: { icon: Clock, label: "No picture now", why: "pictures are kept for 30 days" },
  none: { icon: ImageOff, label: "No picture", why: "the browser didn't send one for this page" },
  unavailable: { icon: ImageOff, label: "No picture", why: "it couldn't be loaded" },
} as const;

/** Where a picture would be, when there's none (slice 6f F4): one quiet line saying why. */
export function NoPicture({ reason }: { reason: keyof typeof NO_PICTURE }) {
  const { icon: Icon, label, why } = NO_PICTURE[reason];
  return (
    <div
      data-slot="no-picture"
      data-reason={reason}
      className="flex max-w-[560px] items-center gap-2 rounded-[10px] bg-muted px-3.5 py-3 text-xs text-muted-foreground"
    >
      <Icon aria-hidden className="size-3.5 shrink-0" strokeWidth={1.75} />
      <span className="min-w-0">
        <span className="font-medium text-foreground">{label}</span> · {why}
      </span>
    </div>
  );
}

/**
 * The browser a running turn used and moved on from (slice 6f F5, F6): one
 * row, its last page as a thumbnail, "Used the browser" and `meta`
 * ("bestbuy.ca · 4 steps · 21s"). It opens to the picture (`children`).
 */
export function BrowserUsed({
  thumb,
  meta,
  children,
}: {
  thumb?: string | null;
  meta: string;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      data-slot="browser-used"
      className="flex max-w-[560px] flex-col rounded-tile bg-card shadow-ring"
    >
      <CollapsibleTrigger
        disabled={!children}
        className="pressable flex min-h-14 items-center gap-3 rounded-tile px-2 py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        {thumb ? (
          <img
            src={thumb}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-9 w-[74px] shrink-0 rounded-[6px] object-cover object-top shadow-ring"
          />
        ) : (
          <span className="flex h-9 w-[74px] shrink-0 items-center justify-center rounded-[6px] bg-muted text-muted-foreground">
            <Monitor aria-hidden className="size-4" strokeWidth={1.75} />
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-medium text-foreground">Used the browser</span>
          <span className="truncate text-xs text-muted-foreground tabular-nums">{meta}</span>
        </span>
        {children ? (
          <ChevronRight
            aria-hidden
            className={cn(
              "mr-1.5 size-4 shrink-0 text-muted-foreground transition-transform duration-150 motion-reduce:transition-none",
              open && "rotate-90",
            )}
            strokeWidth={1.75}
          />
        ) : null}
      </CollapsibleTrigger>
      {children ? (
        <CollapsibleContent className={foldMotion}>
          <div className="px-2 pb-2">{children}</div>
        </CollapsibleContent>
      ) : null}
    </Collapsible>
  );
}

/**
 * Every browser step of a turn, in one card (S-509): "Used the browser · 7
 * steps · 58s", where it went, the picture of its last page (slice 6f), and
 * its first three steps, then "Show 4 more". A step with a picture of its own
 * shows it when pressed (`onSelect`); `selected` is the one shown.
 */
export function ComputerCard({
  meta,
  breadcrumb,
  steps,
  picture,
  selected,
  onSelect,
}: {
  meta: string;
  breadcrumb: string;
  steps: Array<{
    key: string;
    label: string;
    duration?: string;
    failed?: boolean;
    /** It has a picture of its own. */
    pictured?: boolean;
  }>;
  picture?: ReactNode;
  selected?: string | null;
  onSelect?: (key: string) => void;
}) {
  const [all, setAll] = useState(false);
  const shown = all ? steps : steps.slice(0, 3);
  return (
    <div
      data-slot="computer-card"
      className="my-1 flex max-w-[560px] flex-col rounded-tile bg-card shadow-ring"
    >
      <div className="flex items-center gap-2.5 px-3.5 pt-3 pb-1">
        <Monitor aria-hidden className="size-4 shrink-0 text-foreground" strokeWidth={1.75} />
        <span className="flex-1 text-sm font-medium text-foreground">Used the browser</span>
        <span className="text-xs text-hint tabular-nums">{meta}</span>
      </div>
      {breadcrumb ? (
        <div className="pr-3.5 pb-3 pl-10 text-meta text-muted-foreground">{breadcrumb}</div>
      ) : null}
      {picture ? <div className="px-3.5 pb-3.5">{picture}</div> : null}
      <div className="flex flex-col border-t border-border px-3.5 py-1">
        {shown.map((s) => {
          const row = (
            <>
              <span
                className={cn(
                  "min-w-0 flex-1 truncate text-meta",
                  s.failed ? "text-destructive" : "text-copy",
                  selected === s.key && "font-medium text-foreground",
                )}
              >
                {s.label}
              </span>
              <span className="w-8 shrink-0 text-right text-xs text-hint tabular-nums">
                {s.duration}
              </span>
            </>
          );
          return s.pictured && onSelect ? (
            <button
              key={s.key}
              type="button"
              aria-pressed={selected === s.key}
              onClick={() => onSelect(s.key)}
              className="pressable flex min-h-11 items-center gap-2.5 rounded-nav pl-[26px] text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-9"
            >
              {row}
            </button>
          ) : (
            <div key={s.key} className="flex min-h-9 items-center gap-2.5 pl-[26px]">
              {row}
            </div>
          );
        })}
        {steps.length > 3 ? (
          <button
            type="button"
            onClick={() => setAll(!all)}
            className="pressable flex min-h-11 items-center self-start rounded-nav pl-[26px] text-meta font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:min-h-9"
          >
            {all ? "Show fewer" : `Show ${steps.length - 3} more`}
          </button>
        ) : null}
      </div>
    </div>
  );
}
